import {
  auroraPalettes,
  usePalette,
  type AuroraPaletteId,
} from '@ownyou/ui-design-system';
import {
  CenteredColumn,
  LetterStage,
  Modal as AuroraModal,
  TwoPane,
} from '@ownyou/ui-components/layouts';
import { useState } from 'react';

export function AuroraShowcase() {
  const { paletteId, setPalette } = usePalette();
  const [modalOpen, setModalOpen] = useState(false);
  const enabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_AURORA_SHOWCASE === 'true';

  if (!enabled) {
    return (
      <section className="p-6">
        <h1 className="text-xl font-semibold">Not found</h1>
      </section>
    );
  }

  return (
    <main data-testid="aurora-showcase" className="space-y-8 pb-12">
      <CenteredColumn maxWidth={1000}>
        <div className="mb-6">
          <p
            className="text-xs uppercase"
            style={{ color: 'var(--aurora-ink-dim)', fontFamily: 'var(--aurora-font-mono)' }}
          >
            Aurora
          </p>
          <h1
            className="text-4xl font-semibold"
            style={{ color: 'var(--aurora-ink)', fontFamily: 'var(--aurora-font)' }}
          >
            Foundation Showcase
          </h1>
        </div>

        <div className="grid gap-3 sm:grid-cols-5">
          {(Object.keys(auroraPalettes) as AuroraPaletteId[]).map((id) => {
            const palette = auroraPalettes[id];
            return (
              <button
                key={id}
                type="button"
                data-testid={`aurora-palette-${id}`}
                onClick={() => setPalette(id)}
                className="rounded-2xl border p-3 text-left"
                style={{
                  borderColor: id === paletteId ? 'var(--aurora-accent)' : 'var(--aurora-ink-line)',
                  background: 'var(--aurora-card)',
                }}
              >
                <span className="mb-3 block text-sm font-medium" style={{ color: 'var(--aurora-ink)' }}>
                  {palette.label}
                </span>
                <span className="flex gap-1">
                  {palette.swatch.map((color) => (
                    <span key={color} className="h-5 flex-1 rounded-full" style={{ background: color }} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </CenteredColumn>

      <LetterStage step={1} of={4}>
        <h2 className="text-2xl font-semibold" style={{ fontFamily: 'var(--aurora-font)' }}>
          LetterStage
        </h2>
        <p className="mt-2 text-sm" style={{ color: 'var(--aurora-ink-dim)' }}>
          Onboarding and recovery surfaces use this centered stage.
        </p>
      </LetterStage>

      <TwoPane
        left={<PrimitivePanel title="Left pane" body="Navigation or list content." />}
        center={<PrimitivePanel title="Center pane" body="Primary content stays readable and stable." />}
        right={<PrimitivePanel title="Right pane" body="Contextual details render here." />}
      />

      <CenteredColumn maxWidth={760}>
        <button
          type="button"
          data-testid="aurora-modal-open"
          onClick={() => setModalOpen(true)}
          className="rounded-full px-4 py-2 text-sm font-medium"
          style={{ background: 'var(--aurora-accent)', color: 'var(--aurora-card-solid)' }}
        >
          Open modal
        </button>
      </CenteredColumn>

      <AuroraModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Aurora modal">
        <p className="text-sm">This modal comes from the packaged layout primitive.</p>
      </AuroraModal>
    </main>
  );
}

function PrimitivePanel({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="rounded-2xl border p-4"
      style={{
        background: 'var(--aurora-card)',
        borderColor: 'var(--aurora-ink-line)',
        boxShadow: 'var(--aurora-card-shadow)',
      }}
    >
      <h2 className="text-lg font-semibold" style={{ color: 'var(--aurora-ink)' }}>
        {title}
      </h2>
      <p className="mt-2 text-sm" style={{ color: 'var(--aurora-ink-dim)' }}>
        {body}
      </p>
    </div>
  );
}
